// src/views/AccommodationProvider/MaintainBlock.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const MaintainBlock = () => {
  const navigate = useNavigate();
  const { propertyId, blockId } = useParams();
  
  // Get provider information from localStorage
  const providerType = localStorage.getItem('provider_type');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [validationErrors, setValidationErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    block_number: '',
    block_name: '',
    occupation_date: '',
    gender: '',
    no_of_floors: '',
    description: ''
  });

  // Fetch property and block data
  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId || !blockId || regStatus !== '2') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        if (propertyResponse.status === 200) {
          setProperty(propertyResponse.data);
          
          // Fetch block data from API
          try {
            const blockResponse = await axiosInstance.get(`/api/blocks/${blockId}/`);
            if (blockResponse.status === 200) {
              const blockData = blockResponse.data;
              setFormData({
                block_number: blockData.block_number || String(blockId).padStart(2, '0'),
                block_name: blockData.block_name || '',
                occupation_date: blockData.occupation_date || '',
                gender: blockData.gender || '',
                no_of_floors: blockData.no_of_floors || '',
                description: blockData.description || ''
              });
            }
          } catch (blockError) {
            console.error('Error fetching block:', blockError);
            // Fallback to blockId if API fails
            setFormData(prev => ({
              ...prev,
              block_number: String(blockId).padStart(2, '0')
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load block data. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId, blockId, regStatus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Validate field on blur
    const errors = { ...validationErrors };
    
    switch (name) {
      case 'block_name':
        if (!value || !value.trim()) {
          errors.block_name = 'Block name is required';
        } else {
          delete errors.block_name;
        }
        break;
      
      case 'occupation_date':
        if (!value) {
          errors.occupation_date = 'Occupation date is required';
        } else {
          delete errors.occupation_date;
        }
        break;
      
      case 'gender':
        if (!value) {
          errors.gender = 'Gender designation is required';
        } else {
          delete errors.gender;
        }
        break;
      
      case 'no_of_floors':
        if (!value) {
          errors.no_of_floors = 'Number of floors is required';
        } else if (parseInt(value) <= 0) {
          errors.no_of_floors = 'Number of floors must be greater than 0';
        } else if (parseInt(value) > 99) {
          errors.no_of_floors = 'Number of floors cannot exceed 99';
        } else {
          delete errors.no_of_floors;
        }
        break;
      
      default:
        break;
    }
    
    setValidationErrors(errors);
  };

  const validateForm = () => {
    const errors = {};
    
    // Block name validation (required)
    if (!formData.block_name || !formData.block_name.trim()) {
      errors.block_name = 'Block name is required';
    }
    
    // Occupation date validation (required)
    if (!formData.occupation_date) {
      errors.occupation_date = 'Occupation date is required';
    }
    
    // Gender validation (required)
    if (!formData.gender) {
      errors.gender = 'Gender designation is required';
    }
    
    // Number of floors validation (required and must be > 0)
    if (!formData.no_of_floors) {
      errors.no_of_floors = 'Number of floors is required';
    } else if (parseInt(formData.no_of_floors) <= 0) {
      errors.no_of_floors = 'Number of floors must be greater than 0';
    } else if (parseInt(formData.no_of_floors) > 99) {
      errors.no_of_floors = 'Number of floors cannot exceed 99';
    }
    
    // Description is optional, no validation needed
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setAlert({
        type: 'danger',
        message: 'Please correct the errors below before submitting.',
        visible: true
      });
      return;
    }

    try {
      setSubmitting(true);
      
      // Update block via API
      const response = await axiosInstance.put(
        `/api/blocks/${blockId}/`,
        {
          block_name: formData.block_name,
          occupation_date: formData.occupation_date,
          gender: formData.gender,
          no_of_floors: formData.no_of_floors,
          description: formData.description,
          is_configured: true,
          property: propertyId
        }
      );

      if (response.status === 200) {
        setAlert({
          type: 'success',
          message: 'Block updated successfully!',
          visible: true
        });
        
        // Redirect back to configure blocks after 2 seconds
        setTimeout(() => {
          navigate(`/provider-dashboard/configure-blocks/${propertyId}`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating block:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.message || 'Failed to update block. Please try again.',
        visible: true
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}`);
  };

  const getWelcomeMessage = () => {
    if (property) {
      return `Maintain Block - ${property.trading_name || `Property #${property.id}`}`;
    }
    return 'Maintain Block';
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading block data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {representativeName && (
                <p className="text-muted mb-1">Representative: {representativeName}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              <Badge color="secondary">
                Block {formData.block_number}
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleCancel}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Blocks
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Form */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-building me-2 text-primary"></i>
                Block Information
              </CardTitle>
              
              <form onSubmit={handleSubmit} noValidate className="needs-validation">
                <Row>
                  <Col md="6">
                    {/* Block Number */}
                    <div className="form-floating mb-3">
                      <input
                        type="text"
                        className="form-control"
                        id="block_number"
                        name="block_number"
                        value={formData.block_number}
                        placeholder="Block Number"
                        disabled
                        readOnly
                      />
                      <label htmlFor="block_number">
                        Block Number
                      </label>
                      <div className="form-text">System assigned</div>
                    </div>
                  </Col>
                  
                  <Col md="6">
                    {/* Block Name */}
                    <div className="form-floating mb-3">
                      <input
                        type="text"
                        className={`form-control ${validationErrors.block_name ? 'is-invalid' : formData.block_name && !validationErrors.block_name ? 'is-valid' : ''}`}
                        id="block_name"
                        name="block_name"
                        value={formData.block_name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Block Name"
                        required
                      />
                      <label htmlFor="block_name">
                        Block Name <span className="text-danger">*</span>
                      </label>
                      {validationErrors.block_name && (
                        <div className="invalid-feedback">
                          {validationErrors.block_name}
                        </div>
                      )}
                      <div className="form-text">Any descriptive name eg Block A, Protea House, Male Housing</div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    {/* Occupation Date */}
                    <div className="form-floating mb-3">
                      <input
                        type="date"
                        className={`form-control ${validationErrors.occupation_date ? 'is-invalid' : formData.occupation_date && !validationErrors.occupation_date ? 'is-valid' : ''}`}
                        id="occupation_date"
                        name="occupation_date"
                        value={formData.occupation_date}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Occupation Date"
                        required
                      />
                      <label htmlFor="occupation_date">
                        Occupation Date <span className="text-danger">*</span>
                      </label>
                      {validationErrors.occupation_date && (
                        <div className="invalid-feedback">
                          {validationErrors.occupation_date}
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    {/* Gender */}
                    <div className="form-floating mb-3">
                      <select
                        className={`form-select ${validationErrors.gender ? 'is-invalid' : formData.gender && !validationErrors.gender ? 'is-valid' : ''}`}
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="mixed">Mixed</option>
                      </select>
                      <label htmlFor="gender">
                        Gender Designation <span className="text-danger">*</span>
                      </label>
                      {validationErrors.gender && (
                        <div className="invalid-feedback">
                          {validationErrors.gender}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    {/* Number of Floors */}
                    <div className="form-floating mb-3">
                      <input
                        type="number"
                        className={`form-control ${validationErrors.no_of_floors ? 'is-invalid' : formData.no_of_floors && !validationErrors.no_of_floors ? 'is-valid' : ''}`}
                        id="no_of_floors"
                        name="no_of_floors"
                        value={formData.no_of_floors}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Number of Floors"
                        min="1"
                        max="99"
                        required
                      />
                      <label htmlFor="no_of_floors">
                        Number of Floors <span className="text-danger">*</span>
                      </label>
                      {validationErrors.no_of_floors && (
                        <div className="invalid-feedback">
                          {validationErrors.no_of_floors}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Description (Optional) */}
                <div className="form-floating mb-4">
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Description"
                    style={{ height: '120px' }}
                  />
                  <label htmlFor="description">
                    Description (Optional)
                  </label>
                  <div className="form-text">
                    Additional information about this block
                  </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-2">
                  <Button 
                    color="primary" 
                    type="submit"
                    disabled={submitting}
                    className="px-4"
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    type="button"
                    onClick={handleCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Help Card */}
          <Card className="mt-3 border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Field Requirements
              </CardTitle>
              <ul className="small text-muted mb-0">
                <li><strong>Block Number:</strong> Unique identifier for the block (e.g., 01, 02)</li>
                <li><strong>Block Name:</strong> Descriptive name for easy identification</li>
                <li><strong>Occupation Date:</strong> Date when the block was first occupied</li>
                <li><strong>Gender Designation:</strong> Male, Female, or Mixed occupancy</li>
                <li><strong>Number of Floors:</strong> Must be at least 1</li>
                <li><strong>Description:</strong> Optional additional information</li>
              </ul>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MaintainBlock;
